import { useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
	getPasswordChecks,
	useAuth,
	useAuthContext,
	userAtom,
	validatePasswordForForm,
} from '@ekhodealer/ekho-common/auth-client';
import {
	Button,
	Callout,
	ContentWithTooltip,
	Group,
	Link,
	Stack,
	Text,
	TextInput,
} from '@ekhodealer/ekho-common/rev';

import { EyeIcon, EyeOffIcon } from '@common/lib/icons';
import { paths } from '@config/paths';

import { useCompleteProfileSetup } from '../api/useCompleteProfileSetup';

import { SetupHeader } from './SetupHeader';

interface FormData {
	firstName: string;
	lastName: string;
	password: string;
	confirmPassword: string;
}

/**
 * Helper component to display password requirement status
 */
function PasswordRequirement({ met, label }: { met: boolean; label: string }) {
	return (
		<div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
			<Text size='footnote' color={met ? 'success' : 'secondary'}>
				{met ? '✓' : '○'}
			</Text>
			<Text size='footnote' color={met ? 'success' : 'secondary'}>
				{label}
			</Text>
		</div>
	);
}

/**
 * Setup Profile page for invite onboarding.
 * Step 1/3: Complete profile / create password.
 * Works for both invited and migrated users (unified flow).
 */
export default function SetupProfilePage() {
	const { authService } = useAuthContext();
	const user = useAtomValue(userAtom);
	const { user: authUser } = useAuth();
	const navigate = useNavigate();
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);

	// Track if password was already updated (for retry after partial failure)
	const [passwordAlreadyUpdated, setPasswordAlreadyUpdated] = useState(false);
	// Error message for setup failures
	const [setupError, setSetupError] = useState<string | null>(null);

	// Get email from user atom or auth user
	const userEmail = user?.email || authUser?.email || '';

	// If Step 1 (seen below, the password update) succeeded but Step 2 (backend profile completion)
	// failed, a refresh would previously reset `passwordAlreadyUpdated` and re-run Step 1.
	// That can force a stale-session reauth using the old invite token (old temp password),
	// which now no longer matches after the password was changed.
	const passwordUpdatedStorageKey = userEmail
		? `setupProfile.passwordUpdated:${userEmail.toLowerCase()}`
		: null;

	const {
		control,
		getValues,
		handleSubmit,
		watch,
		trigger,
		formState: { errors, isSubmitting, isValid },
	} = useForm<FormData>({
		mode: 'onChange',
		defaultValues: {
			firstName: '',
			lastName: '',
			password: '',
			confirmPassword: '',
		},
	});

	// Get invite token from URL params
	const [searchParams] = useSearchParams();
	const inviteToken = searchParams.get('invite');

	// Restore persisted password-updated state (survives refresh)
	useEffect(() => {
		if (!passwordUpdatedStorageKey) return;
		try {
			const stored = sessionStorage.getItem(passwordUpdatedStorageKey);
			if (stored === 'true') {
				setPasswordAlreadyUpdated(true);
			}
		} catch {
			// Ignore storage access issues (e.g., restricted environments)
		}
	}, [passwordUpdatedStorageKey]);

	// Guard: require invite token unless Step 1 already succeeded (partial failure retry)
	// or user is already authenticated (e.g., arrived from /login redirect).
	useEffect(() => {
		if (inviteToken) return;
		if (authUser) return;
		try {
			const stored = passwordUpdatedStorageKey
				? sessionStorage.getItem(passwordUpdatedStorageKey)
				: null;
			if (stored !== 'true') {
				navigate(paths.noAccess.getHref(), { replace: true });
			}
		} catch {
			navigate(paths.noAccess.getHref(), { replace: true });
		}
	}, [inviteToken, authUser, passwordUpdatedStorageKey, navigate]);

	// Watch password for requirements display and to trigger confirmPassword re-validation
	const password = watch('password');

	// Re-validate confirmPassword when password changes
	useEffect(() => {
		const confirmPasswordValue = getValues('confirmPassword');
		if (confirmPasswordValue) {
			trigger('confirmPassword');
		}
	}, [password, getValues, trigger]);

	// Mutation hook for completing profile setup
	const completeProfileSetupMutation = useCompleteProfileSetup();

	const submitHandler = handleSubmit(async (data: FormData) => {
		// Clear any previous error
		setSetupError(null);

		try {
			// Step 1: Update password client-side via Firebase Auth
			// Skip if already updated on a previous attempt (handles partial failure retry)
			// Skip if no invite token (user authenticated via /login, already has a password)
			if (!passwordAlreadyUpdated && inviteToken) {
				await authService.updateCurrentUserPassword(data.password, {
					email: userEmail,
					password: inviteToken!,
				});
				setPasswordAlreadyUpdated(true);
				// Persist: Step 1 succeeded, so Step 1 must be skipped after refresh.
				if (passwordUpdatedStorageKey) {
					sessionStorage.setItem(passwordUpdatedStorageKey, 'true');
				}
			}

			// Step 2: Complete profile setup on backend (creates/updates user document)
			await completeProfileSetupMutation.mutateAsync({
				email: userEmail,
				firstName: data.firstName,
				lastName: data.lastName,
			});

			// Entire setup step succeeded; clear the persisted flag.
			if (passwordUpdatedStorageKey) {
				sessionStorage.removeItem(passwordUpdatedStorageKey);
			}

			// No manual navigation needed - the mutation invalidates /me query,
			// which triggers InviteAuthGuard to route based on updated onboarding state
		} catch (err) {
			console.error('Failed to complete profile setup:', err);

			if (passwordAlreadyUpdated) {
				// Password was updated but backend failed - inform user clearly
				setSetupError(
					'Your password was updated successfully, but we couldn\'t complete your profile. Please click "Sign up" to try again.',
				);
			} else {
				// Password update failed - user can retry with same credentials
				setSetupError('Failed to complete setup. Please try again.');
			}
		}
	});

	const isSubmitDisabled =
		isSubmitting ||
		!isValid ||
		!userEmail ||
		userEmail.trim() === '' ||
		completeProfileSetupMutation.isPending;

	return (
		<form onSubmit={submitHandler}>
			<Stack itemsSpacing='24'>
				{/* Header */}
				<SetupHeader
					title='Welcome to Ekho!'
					details='Manage your inventory, orders, leads, site, and settings all in one place.'
				/>
				{/* Form Fields */}
				<Stack itemsSpacing='16'>
					{/* First Name */}
					<Controller
						name='firstName'
						control={control}
						rules={{
							required: 'First name is required',
							minLength: {
								value: 1,
								message: 'First name is required',
							},
						}}
						render={({ field: { onChange, onBlur, value } }) => (
							<Stack itemsSpacing='4'>
								<Text size='footnote'>First name</Text>
								<TextInput
									placeholder='Enter your first name'
									value={value}
									onChange={(val) => onChange(val)}
									onBlur={onBlur}
									size='large'
									variant='outline'
									hasError={errors.firstName?.message}
									isDisabled={isSubmitting}
								/>
							</Stack>
						)}
					/>

					{/* Last Name */}
					<Controller
						name='lastName'
						control={control}
						rules={{
							required: 'Last name is required',
							minLength: {
								value: 1,
								message: 'Last name is required',
							},
						}}
						render={({ field: { onChange, onBlur, value } }) => (
							<Stack itemsSpacing='4'>
								<Text size='footnote'>Last name</Text>
								<TextInput
									placeholder='Enter your last name'
									value={value}
									onChange={(val) => onChange(val)}
									onBlur={onBlur}
									size='large'
									variant='outline'
									hasError={errors.lastName?.message}
									isDisabled={isSubmitting}
								/>
							</Stack>
						)}
					/>

					{/* Email (read-only) */}
					<Stack itemsSpacing='4'>
						<Text size='footnote'>Email</Text>
						<ContentWithTooltip
							content={
								<TextInput
									value={userEmail}
									isReadOnly
									variant='outline'
									size='large'
									placeholder='Email'
								/>
							}
							tooltipContent="This email was used to invite you to Ekho and can't be changed during account setup."
							tooltipProps={{ placement: 'top' }}
						/>
					</Stack>

					{/* Password */}
					<Controller
						name='password'
						control={control}
						rules={{
							validate: validatePasswordForForm,
						}}
						render={({ field: { onChange, onBlur, value } }) => {
							const checks = getPasswordChecks(value);
							return (
								<Stack itemsSpacing='4'>
									<Text size='footnote'>Create a password</Text>
									<TextInput
										placeholder='Create a password'
										value={value}
										onChange={(val) => onChange(val)}
										onBlur={onBlur}
										size='large'
										variant='outline'
										type={showPassword ? 'text' : 'password'}
										hasError={errors.password?.message}
										isDisabled={isSubmitting}
										rightContent={
											<button
												type='button'
												className='auth-password-toggle'
												onClick={() => setShowPassword(!showPassword)}
												aria-label={
													showPassword ? 'Hide password' : 'Show password'
												}>
												{showPassword ? <EyeIcon /> : <EyeOffIcon />}
											</button>
										}
									/>
									{value && (
										<div style={{ marginLeft: '16px' }}>
											<Stack itemsSpacing='2'>
												<PasswordRequirement
													met={checks.minLength}
													label='8 or more characters'
												/>
												<PasswordRequirement
													met={checks.hasUppercase && checks.hasLowercase}
													label='Uppercase and lowercase letters'
												/>
												<PasswordRequirement
													met={checks.hasNumber}
													label='At least one number'
												/>
												<PasswordRequirement
													met={checks.hasSpecialChar}
													label='At least one special character (e.g., ! @ # $ %)'
												/>
											</Stack>
										</div>
									)}
								</Stack>
							);
						}}
					/>

					{/* Confirm Password */}
					<Controller
						name='confirmPassword'
						control={control}
						rules={{
							required: 'Please confirm your password',
							validate: (value) =>
								value === getValues('password') || 'Passwords do not match',
						}}
						render={({ field: { onChange, onBlur, value } }) => (
							<Stack itemsSpacing='4'>
								<Text size='footnote'>Confirm password</Text>
								<TextInput
									placeholder='Confirm password'
									value={value}
									onChange={(val) => onChange(val)}
									onBlur={onBlur}
									size='large'
									variant='outline'
									type={showConfirmPassword ? 'text' : 'password'}
									hasError={errors.confirmPassword?.message}
									isDisabled={isSubmitting}
									rightContent={
										<button
											type='button'
											className='auth-password-toggle'
											onClick={() =>
												setShowConfirmPassword(!showConfirmPassword)
											}
											aria-label={
												showConfirmPassword
													? 'Hide password'
													: 'Show password'
											}>
											{showConfirmPassword ? <EyeIcon /> : <EyeOffIcon />}
										</button>
									}
								/>
							</Stack>
						)}
					/>
				</Stack>

				{/* Error Message */}
				{setupError && <Callout variant='error' message={setupError} />}

				{/* Submission */}
				<Stack itemsSpacing='16'>
					<Button
						size='large'
						isFullWidth
						isLoading={isSubmitting || completeProfileSetupMutation.isPending}
						isDisabled={isSubmitDisabled}
						type='submit'
						onPress={() => submitHandler()}>
						Sign up
					</Button>
					<Group itemsAlignX='start' itemsSpacing='8'>
						<Text size='bodySmall' align='left'>
							Already have an account?
						</Text>
						<Button variant='underlined' size='small' href={paths.login.getHref()}>
							Log in
						</Button>
					</Group>
				</Stack>

				{/* Footer */}
				<Text size='footnote' color='secondary' align='left'>
					By signing up for an Ekho account, you agree to our{' '}
					<Link
						href='https://www.ekho.com/legal/terms-conditions'
						target='_blank'
						rel='noopener noreferrer'
						style={{ color: 'inherit', textDecoration: 'underline' }}>
						Privacy Policy
					</Link>{' '}
					and{' '}
					<Link
						href='https://www.ekho.com/legal/terms-conditions'
						target='_blank'
						rel='noopener noreferrer'
						style={{ color: 'inherit', textDecoration: 'underline' }}>
						Terms of Service
					</Link>
				</Text>
			</Stack>
		</form>
	);
}
